/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import type { EmberChangeset } from "ember-changeset";
import type {
  ForcModelPojo,
  Payload as ForcPayload
} from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import journalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import DisorderHistoryDeclaredJournalEvent from "parog-web/models/disorder-history-declared-journal-event";

interface ThymicEpisode {
  occurredAt?: Date;
}

interface DisorderHistoryModelType {
  thymicEpisodes: ThymicEpisode[];
  firstThymicEpisode?: ThymicEpisode;
}

interface DisorderHistoryPayloadType {
  thymicEpisodes: ThymicEpisode[];
  firstThymicEpisode?: ThymicEpisode;
}

type DisorderHistoryEditionFormModel = ForcModelPojo & DisorderHistoryModelType;
type DisorderHistoryEditionFormPayload = ForcPayload &
  DisorderHistoryPayloadType;

export default class DisorderHistoryEditionFormComponent extends JournalEventBasedComponent {
  static fullComponentId = "disorder-history/edition";
  static forcDisplayName = "Historique du trouble avant accompagnement";

  static journalEventClass = DisorderHistoryDeclaredJournalEvent;

  journalEventProperties = [
    "occurredAt",
    "numberOfDepressiveEpisode",
    "numberOfHypomaniacEpisode",
    "numberOfManiacEpisode",
    "numberOfEpisodeCommentary",
    "numberOfHospitalization",
    "numberOfTs",
    "numberOfTsWithThymicEpisode",
    "numberOfTsCommentary",
    "presenceOfRapidCycles",
    "presenceOfRapidCyclesCommentary",
    "thymicEpisodes",
    "firstThymicEpisode"
  ];

  retrieveJournalEvent = journalEventRetrievalStrategies.sourceJournalEvent;

  retrieveModel(): DisorderHistoryEditionFormModel {
    const model = super.retrieveModel() as DisorderHistoryEditionFormModel;

    if (this.isNewMode) {
      model.thymicEpisodes = [];
      model.firstThymicEpisode = {};
      model.occurredAt = new Date();
    } else {
      // Extract the first thymic episode
      model.firstThymicEpisode = this.retrieveFirstThymicEpisode(
        model.thymicEpisodes
      );
      model.thymicEpisodes = model.thymicEpisodes.filter(
        episode => episode != model.firstThymicEpisode
      );
    }

    return model;
  }

  computePayload(): ForcPayload {
    const payload = super.computePayload() as DisorderHistoryEditionFormPayload;
    // Execute every sub-changeset to apply changes to our model
    this.thymicEpisodeChangesets.forEach(changeset => changeset.execute());
    // Save the first thymic episode
    if (payload.firstThymicEpisode) {
      payload.thymicEpisodes.push(payload.firstThymicEpisode);
    }

    return payload;
  }

  /**
   * Store the sub changesets created for the thymic episodes.
   */
  @tracked
  private thymicEpisodeChangesets: EmberChangeset[] = [];

  @action
  onThymicEpisodeCreated(changeset: EmberChangeset): void {
    this.thymicEpisodeChangesets.push(changeset);
  }

  @action
  onThymicEpisodeDestroyed(changeset: EmberChangeset): void {
    this.thymicEpisodeChangesets = this.thymicEpisodeChangesets.filter(
      e => e != changeset
    );
  }

  get isDirty(): boolean {
    if (this.changeset.get("isDirty")) {
      return true;
    }
    for (const changeset of this.thymicEpisodeChangesets) {
      if (changeset.get("isDirty")) {
        return true;
      }
    }

    return false;
  }

  private retrieveFirstThymicEpisode(
    thymicEpisodes: ThymicEpisode[]
  ): ThymicEpisode | undefined {
    return thymicEpisodes.slice().sort((a, b) => {
      return (
        new Date(a.occurredAt as Date).getTime() -
        new Date(b.occurredAt as Date).getTime()
      );
    })[0];
  }
}
